import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Receipt,
  Search,
  Filter,
  Download,
  Calendar,
  TrendingUp,
  TrendingDown,
  ArrowUpRight,
  ArrowDownLeft,
  DollarSign,
  CreditCard,
  Smartphone,
  Coffee,
  Car,
  ShoppingBag,
  Home,
  Plane,
  MoreVertical,
  ChevronRight,
  Menu,
  Loader2,
  RefreshCw,
  ChevronLeft,
  Utensils,
  Zap,
  Heart,
  Briefcase,
  Gift,
  Building,
  X,
  Copy,
  CheckCircle,
  Clock,
  MapPin,
  Tag,
  Hash,
  FileText,
  AlertCircle
} from 'lucide-react'
import { TransactionsAPI, AccountsAPI, extractArray } from '../services/api'

const getCategoryIcon = (categoryName) => {
  const name = categoryName?.toLowerCase() || ''
  if (name.includes('food') || name.includes('dining') || name.includes('restaurant')) return Utensils
  if (name.includes('transport') || name.includes('gas') || name.includes('uber')) return Car
  if (name.includes('shopping') || name.includes('retail')) return ShoppingBag
  if (name.includes('housing') || name.includes('rent') || name.includes('mortgage')) return Home
  if (name.includes('travel') || name.includes('flight') || name.includes('hotel')) return Plane
  if (name.includes('utilities') || name.includes('electric') || name.includes('internet')) return Zap
  if (name.includes('salary') || name.includes('income') || name.includes('payroll')) return DollarSign
  if (name.includes('transfer')) return ArrowUpRight
  if (name.includes('health') || name.includes('medical')) return Heart
  if (name.includes('entertainment') || name.includes('subscription')) return Smartphone
  if (name.includes('business')) return Briefcase
  if (name.includes('gift')) return Gift
  if (name.includes('atm') || name.includes('bank')) return Building
  return Receipt
}

export default function TransactionsPage({ onMenuClick }) {
  const { t, i18n } = useTranslation(['transactions', 'common'])
  const [transactions, setTransactions] = useState([])
  const [accounts, setAccounts] = useState([])
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const [searchTerm, setSearchTerm] = useState('')
  const [selectedAccount, setSelectedAccount] = useState('all')
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [dateRange, setDateRange] = useState('all')
  const [transactionType, setTransactionType] = useState('all')

  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalItems, setTotalItems] = useState(0)

  const [summary, setSummary] = useState({ income: 0, expenses: 0, net: 0 })
  const [selectedTransaction, setSelectedTransaction] = useState(null)
  const [notification, setNotification] = useState(null)

  const showNotification = (message) => {
    setNotification(message)
    setTimeout(() => setNotification(null), 2000)
  }

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text)
    showNotification(t('copiedToClipboard', { label }))
  }

  useEffect(() => {
    fetchInitialData()
  }, [])

  useEffect(() => {
    fetchTransactions()
  }, [page, selectedAccount, selectedCategory, dateRange, transactionType])

  const fetchInitialData = async () => {
    try {
      const [accountsRes, categoriesRes, summaryRes] = await Promise.all([
        AccountsAPI.getAll(),
        TransactionsAPI.getCategories(),
        TransactionsAPI.getSummary('month')
      ])

      if (accountsRes.success) {
        const accountData = extractArray(accountsRes, 'accounts')
        setAccounts([{ id: 'all', account_name: t('allAccounts') }, ...accountData])
      }
      if (categoriesRes.success) {
        const categoryData = extractArray(categoriesRes)
        setCategories([{ id: 'all', name: t('allCategories') }, ...categoryData])
      }
      if (summaryRes.success) {
        setSummary({
          income: summaryRes.data?.totalIncome || 0,
          expenses: Math.abs(summaryRes.data?.totalExpenses || 0),
          net: summaryRes.data?.netFlow || 0
        })
      }
    } catch (err) {
      console.error('Error fetching initial data:', err)
    }
  }

  const fetchTransactions = async () => {
    setLoading(true)
    setError(null)
    try {
      const params = {
        page,
        limit: 20
      }

      if (selectedAccount !== 'all') {
        params.accountId = selectedAccount
      }
      if (selectedCategory !== 'all') {
        params.categoryId = selectedCategory
      }
      if (transactionType !== 'all') {
        params.type = transactionType
      }
      if (dateRange !== 'all') {
        const now = new Date()
        let startDate = new Date()
        switch (dateRange) {
          case 'today':
            startDate.setHours(0, 0, 0, 0)
            break
          case 'week':
            startDate.setDate(now.getDate() - 7)
            break
          case 'month':
            startDate.setMonth(now.getMonth() - 1)
            break
          case 'year':
            startDate.setFullYear(now.getFullYear() - 1)
            break
        }
        params.startDate = startDate.toISOString().split('T')[0]
      }

      const res = await TransactionsAPI.getAll(params)
      if (res.success) {
        setTransactions(res.data)
        if (res.pagination) {
          setTotalPages(res.pagination.totalPages)
          setTotalItems(res.pagination.totalItems)
        }
      }
    } catch (err) {
      console.error('Error fetching transactions:', err)
      setError(t('errors.loadFailed'))
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      fetchTransactions()
      return
    }

    setLoading(true)
    try {
      const res = await TransactionsAPI.search(searchTerm)
      if (res.success) {
        setTransactions(res.data)
        setTotalPages(1)
        setTotalItems(res.data.length)
      }
    } catch (err) {
      console.error('Error searching transactions:', err)
    } finally {
      setLoading(false)
    }
  }

  const filteredTransactions = transactions.filter(tx => {
    if (!searchTerm) return true
    const search = searchTerm.toLowerCase()
    return (
      tx.description?.toLowerCase().includes(search) ||
      tx.merchant_name?.toLowerCase().includes(search) ||
      tx.category_name?.toLowerCase().includes(search)
    )
  })

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    return date.toLocaleDateString(i18n.language, { month: 'short', day: 'numeric', year: 'numeric' })
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Top Bar */}
      <div className="border-b border-slate-200 bg-white sticky top-0 z-10">
        <div className="px-4 md:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 flex-1">
              <button
                onClick={onMenuClick}
                className="lg:hidden p-2 hover:bg-slate-100 rounded-lg transition-colors flex-shrink-0"
              >
                <Menu className="w-6 h-6 text-slate-700" />
              </button>

              <div className="flex-1">
                <h1 className="text-xl md:text-2xl font-semibold text-slate-900">{t('title')}</h1>
                <p className="text-xs md:text-sm text-slate-500 mt-0.5 hidden sm:block">{t('subtitle')}</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={fetchTransactions}
                disabled={loading}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <RefreshCw className={`w-5 h-5 text-slate-600 ${loading ? 'animate-spin' : ''}`} />
              </button>
              <button className="flex items-center gap-2 px-3 md:px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-lg transition-all text-xs md:text-sm font-medium">
                <Download className="w-4 h-4" />
                <span className="hidden sm:inline">{t('exportButton')}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="p-4 md:p-6 lg:p-8 max-w-[1600px] mx-auto">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-6 mb-6 md:mb-8">
          <div className="bg-white border border-slate-200 rounded-lg md:rounded-xl p-4 md:p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs text-slate-500 font-medium">{t('summary.totalIncome')}</p>
              <div className="w-8 h-8 bg-emerald-50 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-4 h-4 text-emerald-600" />
              </div>
            </div>
            <p className="text-xl md:text-2xl font-bold text-slate-900 notranslate" translate="no">
              ${summary.income.toLocaleString(i18n.language, { minimumFractionDigits: 2 })}
            </p>
            <p className="text-xs text-slate-500 mt-1">{t('summary.thisMonth')}</p>
          </div>

          <div className="bg-white border border-slate-200 rounded-lg md:rounded-xl p-4 md:p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs text-slate-500 font-medium">{t('summary.totalExpenses')}</p>
              <div className="w-8 h-8 bg-red-50 rounded-lg flex items-center justify-center">
                <TrendingDown className="w-4 h-4 text-red-600" />
              </div>
            </div>
            <p className="text-xl md:text-2xl font-bold text-slate-900 notranslate" translate="no">
              ${summary.expenses.toLocaleString(i18n.language, { minimumFractionDigits: 2 })}
            </p>
            <p className="text-xs text-slate-500 mt-1">{t('summary.thisMonth')}</p>
          </div>

          <div className="bg-white border border-slate-200 rounded-lg md:rounded-xl p-4 md:p-5 sm:col-span-2 md:col-span-1">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs text-slate-500 font-medium">{t('summary.netFlow')}</p>
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                summary.net >= 0 ? 'bg-blue-50' : 'bg-orange-50'
              }`}>
                <DollarSign className={`w-4 h-4 ${summary.net >= 0 ? 'text-blue-600' : 'text-orange-600'}`} />
              </div>
            </div>
            <p className={`text-xl md:text-2xl font-bold notranslate ${summary.net >= 0 ? 'text-slate-900' : 'text-orange-600'}`} translate="no">
              {summary.net >= 0 ? '+' : ''}${Math.abs(summary.net).toLocaleString(i18n.language, { minimumFractionDigits: 2 })}
            </p>
            <p className="text-xs text-slate-500 mt-1">{t('summary.thisMonth')}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white border border-slate-200 rounded-lg md:rounded-xl p-4 md:p-6 mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 md:gap-4">
            {/* Search */}
            <div className="relative lg:col-span-2">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
              <input
                type="text"
                placeholder={t('search')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-slate-900 transition-colors"
              />
            </div>

            {/* Account Filter */}
            <select
              value={selectedAccount}
              onChange={(e) => { setSelectedAccount(e.target.value); setPage(1); }}
              className="px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:border-slate-900 transition-colors"
            >
              {accounts.map(account => (
                <option key={account.id} value={account.id}>
                  {account.account_name || account.type_name || 'Account'}
                </option>
              ))}
            </select>

            {/* Category Filter */}
            <select
              value={selectedCategory}
              onChange={(e) => { setSelectedCategory(e.target.value); setPage(1); }}
              className="px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:border-slate-900 transition-colors"
            >
              {categories.map(category => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>

            {/* Date Range */}
            <select
              value={dateRange}
              onChange={(e) => { setDateRange(e.target.value); setPage(1); }}
              className="px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:border-slate-900 transition-colors"
            >
              <option value="all">{t('periods.allTime')}</option>
              <option value="today">{t('periods.today')}</option>
              <option value="week">{t('periods.thisWeek')}</option>
              <option value="month">{t('periods.thisMonth')}</option>
              <option value="year">{t('periods.thisYear')}</option>
            </select>
          </div>

          {/* Quick Filters */}
          <div className="flex items-center gap-2 mt-4 pt-4 border-t border-slate-100">
            <span className="text-xs text-slate-500 mr-2">{t('type')}</span>
            {[
              { value: 'all', label: t('filters.all') },
              { value: 'credit', label: t('filters.income') },
              { value: 'debit', label: t('filters.expenses') }
            ].map((type) => (
              <button
                key={type.value}
                onClick={() => { setTransactionType(type.value); setPage(1); }}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  transactionType === type.value
                    ? 'bg-slate-900 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {type.label}
              </button>
            ))}
          </div>
        </div>

        {/* Transactions List */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-900">
              {t('transactionsFound', { count: totalItems })}
            </h3>
            {totalPages > 1 && (
              <p className="text-xs text-slate-500">
                {t('pageOf', { page, total: totalPages })}
              </p>
            )}
          </div>

          {loading && transactions.length === 0 ? (
            <div className="p-12 text-center">
              <Loader2 className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-4" />
              <p className="text-slate-500">{t('loading')}</p>
            </div>
          ) : error ? (
            <div className="p-12 text-center">
              <div className="w-16 h-16 bg-red-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Receipt className="w-8 h-8 text-red-400" />
              </div>
              <h3 className="text-sm font-semibold text-slate-900 mb-1">{error}</h3>
              <button
                onClick={fetchTransactions}
                className="mt-4 px-4 py-2 bg-slate-900 text-white rounded-lg text-sm hover:bg-slate-800"
              >
                {t('common:tryAgain')}
              </button>
            </div>
          ) : filteredTransactions.length === 0 ? (
            <div className="p-12 text-center">
              <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Receipt className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="text-sm font-semibold text-slate-900 mb-1">{t('noTransactionsFound')}</h3>
              <p className="text-xs text-slate-500">{t('adjustCriteria')}</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-200">
              {filteredTransactions.map((transaction, index) => {
                const IconComponent = getCategoryIcon(transaction.category_name || transaction.transaction_type)
                const isCredit = parseFloat(transaction.amount) > 0

                return (
                  <motion.div
                    key={transaction.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.03 }}
                    onClick={() => setSelectedTransaction(transaction)}
                    className="px-4 md:px-6 py-4 hover:bg-slate-50 transition-colors cursor-pointer"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 md:gap-4 flex-1 min-w-0">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                          isCredit
                            ? 'bg-emerald-50 text-emerald-600'
                            : 'bg-slate-100 text-slate-600'
                        }`}>
                          <IconComponent className="w-5 h-5" />
                        </div>

                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-semibold text-slate-900 truncate">
                            {transaction.description || transaction.merchant_name || 'Transaction'}
                          </h4>
                          <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5 flex-wrap">
                            {transaction.merchant_name && (
                              <>
                                <span className="truncate">{transaction.merchant_name}</span>
                                <span className="hidden sm:inline">•</span>
                              </>
                            )}
                            {transaction.category_name && (
                              <>
                                <span className="hidden sm:inline">{transaction.category_name}</span>
                                <span className="hidden sm:inline">•</span>
                              </>
                            )}
                            <span className="hidden md:inline truncate">{transaction.account_name}</span>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-4 md:gap-6 flex-shrink-0">
                        <div className="text-right">
                          <p className={`text-sm font-bold notranslate ${
                            isCredit ? 'text-emerald-600' : 'text-slate-900'
                          }`} translate="no">
                            {isCredit ? '+' : '-'}${Math.abs(parseFloat(transaction.amount)).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                          </p>
                          <p className="text-xs text-slate-500 mt-0.5">{formatDate(transaction.transaction_date)}</p>
                        </div>

                        <button className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors hidden sm:block">
                          <ChevronRight className="w-4 h-4 text-slate-400" />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                )
              })}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1 || loading}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
                {t('previous')}
              </button>

              <div className="flex items-center gap-2">
                {[...Array(Math.min(5, totalPages))].map((_, i) => {
                  const pageNum = i + 1
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setPage(pageNum)}
                      className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${
                        page === pageNum
                          ? 'bg-slate-900 text-white'
                          : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  )
                })}
              </div>

              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages || loading}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('next')}
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Transaction Detail Modal */}
      <AnimatePresence>
        {selectedTransaction && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedTransaction(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className={`px-6 py-5 ${parseFloat(selectedTransaction.amount) > 0 ? 'bg-emerald-50' : 'bg-slate-50'}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
                      parseFloat(selectedTransaction.amount) > 0 ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-200 text-slate-600'
                    }`}>
                      {React.createElement(getCategoryIcon(selectedTransaction.category_name || selectedTransaction.transaction_type), { className: 'w-7 h-7' })}
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-slate-900">
                        {selectedTransaction.description || selectedTransaction.merchant_name || 'Transaction'}
                      </h2>
                      <p className="text-sm text-slate-500 mt-0.5">
                        {selectedTransaction.merchant_name || selectedTransaction.transaction_type}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedTransaction(null)}
                    className="p-2 hover:bg-white/50 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5 text-slate-500" />
                  </button>
                </div>

                {/* Amount */}
                <div className="mt-4 pt-4 border-t border-slate-200/50">
                  <p className="text-sm text-slate-500 mb-1">{t('details.amount')}</p>
                  <p className={`text-3xl font-bold notranslate ${parseFloat(selectedTransaction.amount) > 0 ? 'text-emerald-600' : 'text-slate-900'}`} translate="no">
                    {parseFloat(selectedTransaction.amount) > 0 ? '+' : '-'}${Math.abs(parseFloat(selectedTransaction.amount)).toLocaleString(i18n.language, { minimumFractionDigits: 2 })}
                  </p>
                </div>
              </div>

              {/* Details */}
              <div className="p-6 space-y-4">
                {/* Transaction ID */}
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Hash className="w-5 h-5 text-slate-400" />
                    <div>
                      <p className="text-xs text-slate-500">{t('details.transactionId')}</p>
                      <p className="text-sm font-mono text-slate-900 notranslate" translate="no">{selectedTransaction.id?.slice(0, 8)}...{selectedTransaction.id?.slice(-4)}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => copyToClipboard(selectedTransaction.id, t('details.transactionId'))}
                    className="p-2 hover:bg-slate-200 rounded-lg transition-colors"
                  >
                    <Copy className="w-4 h-4 text-slate-500" />
                  </button>
                </div>

                {/* Date & Time */}
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <Clock className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-xs text-slate-500">{t('details.date')}</p>
                    <p className="text-sm font-medium text-slate-900">
                      {new Date(selectedTransaction.transaction_date).toLocaleDateString(i18n.language, {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                      {` ${t('at')} `}
                      {new Date(selectedTransaction.transaction_date).toLocaleTimeString(i18n.language, {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                </div>

                {/* Category */}
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <Tag className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-xs text-slate-500">{t('details.category')}</p>
                    <p className="text-sm font-medium text-slate-900">
                      {selectedTransaction.category_name || t('uncategorized')}
                    </p>
                  </div>
                </div>

                {/* Account */}
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <CreditCard className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-xs text-slate-500">{t('details.account')}</p>
                    <p className="text-sm font-medium text-slate-900">
                      {selectedTransaction.account_name} {selectedTransaction.account_number && `(••${selectedTransaction.account_number.slice(-4)})`}
                    </p>
                  </div>
                </div>

                {/* Status */}
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-emerald-500" />
                  <div>
                    <p className="text-xs text-slate-500">{t('details.status')}</p>
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-600">
                        <div className="w-1.5 h-1.5 bg-emerald-600 rounded-full"></div>
                        {t(`status.${selectedTransaction.status?.toLowerCase() || 'completed'}`)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Reference/Memo */}
                {selectedTransaction.reference_number && (
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-slate-400" />
                      <div>
                        <p className="text-xs text-slate-500">{t('details.reference')}</p>
                        <p className="text-sm font-mono text-slate-900">{selectedTransaction.reference_number}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => copyToClipboard(selectedTransaction.reference_number, t('details.reference'))}
                      className="p-2 hover:bg-slate-200 rounded-lg transition-colors"
                    >
                      <Copy className="w-4 h-4 text-slate-500" />
                    </button>
                  </div>
                )}

                {/* Balance After */}
                {selectedTransaction.balance_after && (
                  <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <DollarSign className="w-5 h-5 text-slate-400" />
                    <div>
                      <p className="text-xs text-slate-500">{t('details.balance')}</p>
                      <p className="text-sm font-medium text-slate-900 notranslate" translate="no">
                        ${parseFloat(selectedTransaction.balance_after).toLocaleString(i18n.language, { minimumFractionDigits: 2 })}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-slate-200 flex gap-3">
                <button
                  onClick={() => {
                    const details = `Transaction: ${selectedTransaction.description || selectedTransaction.merchant_name}\nAmount: ${parseFloat(selectedTransaction.amount) > 0 ? '+' : '-'}$${Math.abs(parseFloat(selectedTransaction.amount)).toFixed(2)}\nDate: ${new Date(selectedTransaction.transaction_date).toLocaleDateString(i18n.language)}\nID: ${selectedTransaction.id}`
                    copyToClipboard(details, t('copyDetails'))
                  }}
                  className="flex-1 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                >
                  <Copy className="w-4 h-4" />
                  {t('copyDetails')}
                </button>
                <button
                  onClick={() => setSelectedTransaction(null)}
                  className="flex-1 py-2.5 bg-slate-900 hover:bg-slate-800 text-white rounded-lg text-sm font-medium transition-colors"
                >
                  {t('common:close')}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Notification Toast */}
      <AnimatePresence>
        {notification && (
          <motion.div
            initial={{ opacity: 0, y: 50, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 50, scale: 0.9 }}
            className="fixed bottom-6 right-6 z-[60] px-5 py-3 bg-slate-900 text-white rounded-xl shadow-lg flex items-center gap-3"
          >
            <CheckCircle className="w-5 h-5" />
            <span className="text-sm font-medium">{notification}</span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
